/**
 * The app shell (ux.md §2): one console, one left nav, seven top-level areas, a global Send button
 * and a ⌘K palette.
 *
 * Admin and Developer are AREAS INSIDE this console, never separate products. That is the "one
 * console, one currency, one API surface" principle — competitors split admin into a second app and
 * the seam shows up as two navigations, two mental models, and settings that disagree.
 *
 * PERSONAL ORGS RENDER WITH HIDDEN ORG CHROME. A solo user with one personal org should never see
 * an org switcher, an org name badge, or a members count: to them there is no organization, there
 * is just their account. The chrome appears when a second org does.
 *
 * Areas whose feature has not landed are still in the nav — the map of the product is part of the
 * product — but they say so with a "Soon" mark and resolve to the dashboard (routes/index.tsx). A
 * nav that hides the areas it does not have yet teaches people the product is smaller than it is;
 * one that pretends they work teaches people it lies.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Avatar, Icon, Kbd, Wordmark, applyResolvedAccent } from '@flowza/ui'
import { api, apiConfigured } from '../../api/client'
import { usePhotoUrl } from '../../api/photo'
import { ReleaseGapBanner } from './ReleaseGap'
import { useWorkflow } from '../../auth/workflow'
import { signOut, useSession } from '../../auth/session'
import { InternationalNav, internationalPaletteAreas } from './InternationalNav'
import { AreaLink, SHELF_AREAS, TEAM_AREAS, type Area, type AreaGroup, type Workspace } from './nav'
import { UNKNOWN_OFFERING, mayInvite, offersAny, usePermissions } from '../../auth/permissions'
import { ADMIN_CONSOLE_KEYS } from './admin/orgSections'
import { applyTheme, cacheTheme, cachedTheme, isUiTheme, type UiTheme } from '../../theme'
import { useScrollResetOnNavigate } from '../scroll-reset'

export type { Area, Workspace } from './nav'

export type Org = {
  readonly id: string
  readonly name: string
  readonly type: 'personal' | 'team' | 'isv_parent' | 'isv_child'
}

export const AREAS: readonly Area[] = [
  { to: '/app', label: 'Dashboard', icon: 'home', group: 'workspace', end: true, ready: true },
  // READY, and second in the nav on purpose — above Documents, because for most people in an
  // organization the answer to "why am I here" is something waiting on THEM, not something they
  // are about to send. Until this existed the console could only show what you sent, and an
  // approver with three agreements outstanding had to go back to their email to find their own
  // work. `ux.md` §6.1 calls the console an action-needed inbox; this is the screen that makes
  // that sentence true for a recipient rather than only for a sender.
  { to: '/app/inbox', label: 'Inbox', icon: 'inbox', group: 'workspace', ready: true },
  { to: '/app/documents', label: 'Documents', icon: 'file-text', group: 'workspace', ready: true },
  // READY. A template list, and the composer as its editor — `ux.md` §6.2's "a template IS a saved
  // composer state", so saving one lives on the envelope and there is no second editing surface.
  //
  // Line 62 puts four things in this area and this is two of them. The other two are named ON the
  // screen rather than left for a customer to go looking for: public links are the product's only
  // path by which a STRANGER mints an envelope and arrive with Turnstile, per-link caps and their
  // own rate-limit lane; bulk send is tagged V1 in the same section. Reports set this precedent —
  // three of four shipped, the fourth stated where it is missing.
  {
    to: '/app/templates',
    label: 'Templates',
    icon: 'template',
    group: 'workspace',
    ready: true,
    // `GET /v1/templates` is `templates.create` (route-permissions.test.ts), which a viewer and a
    // billing admin do not hold — their Templates screen was one 403 (ADR 0196).
    requires: ['templates.create'],
  },
  // READY. The book, its signing history, and the writes behind it (P7-12). What ux.md line 63
  // also names — a per-contact default signing language and authentication method — is not editable
  // here, and the screen says so at the bottom rather than leaving somebody to look for it.
  { to: '/app/contacts', label: 'Contacts', icon: 'users', group: 'workspace', ready: true },
  // READY. Volume, completion rate and time-to-sign, over `GET /v1/reports/envelopes`.
  //
  // `ux.md` §2 lists a fourth thing in this area — per-team usage. Teams EXIST now (ADR 0066) and
  // the report is already NARROWED to the caller's own, because `envelopeReport` takes the same
  // workspace scope every list does. What it does not yet do is GROUP BY team, and the screen says
  // so rather than implying the number is organization-wide when it is not.
  { to: '/app/reports', label: 'Reports', icon: 'chart', group: 'workspace', ready: true },
  // The two SHELVES (founder decisions D3 and D9) used to sit here, below the working areas. They
  // are GONE from the sidebar — the founder's ask of 2026-09-16, because the Inbox's Archived and
  // Deleted tabs hold the same envelopes with the same restore, and two doors to one room is one
  // door too many. The routes and the ⌘K entries live on in `nav.tsx` as `SHELF_AREAS`; the
  // library's own shelf rail is still how you reach them from Documents.
  // The TEAM group lives in `nav.tsx`, because the International sidebar draws the same two
  // entries and the two modules must not import each other.
  ...TEAM_AREAS,
  { to: '/app/settings', label: 'Settings', icon: 'sliders', group: 'organization', ready: true },
  // READY, and this one is a judgement rather than a completion. `ux.md` line 66 defines Admin as
  // nine things and one of them — the audit log — is built. The label used to say "Soon", which was
  // a defensible reading of "the area is not done" and a poor one for the person holding the
  // product: the screen is REACHABLE, it works, and an admin who wants to read the security log was
  // being told to come back later while the answer sat one click away behind a route that resolved
  // to the dashboard.
  //
  // The claim moves to where it can be precise. The page itself names all eight things that are not
  // built and where the two that exist elsewhere live, which a four-letter badge cannot do.
  //
  // OFFERED BY PERMISSION (ADR 0196). Every screen in this console carries a catalog key — the same
  // key the API gates its route with — and the area is offered to somebody who holds at least one
  // of them. `ux.md` §2 has said "visible only with admin role" since the corpus was written; until
  // 2026-09-16 the nav did not, and a viewer was offered fourteen screens that would each refuse
  // them. What is offered, never what is allowed: a deep link still lands on a screen that says
  // in a sentence who may open it.
  {
    to: '/app/admin',
    label: 'Admin',
    icon: 'shield',
    group: 'organization',
    ready: true,
    requires: ADMIN_CONSOLE_KEYS,
  },
  // Developer is a TENANT area (ux.md §2, MVP): API keys, webhooks + tester, event/request log,
  // docs, sandbox switch — in-console, never a second site login, which is the anti-DocuSign stance
  // the same section names. It is "Soon" because that portal lands in Phase 8, not because tenants
  // should not have it. What used to sit here was the deployment-diagnostics page, an operator's
  // screen that named the Supabase host and reported the isolation probe to whoever was signed in;
  // it now lives at an unlisted, build-flagged route (routes/index.tsx) and ships in no tenant build.
  // READY, on one of the five things ux.md line 62 lists — and the thinnest flip in this nav, so
  // the reasoning is written down rather than assumed.
  //
  // The one that ships is the FOUNDATIONAL one: until `api_keys` existed, an API-first product's
  // API could be reached by nothing but its own web app. Webhooks, the event and request log, and
  // in-console docs are named on the screen as absent. Test mode is named separately, because it is
  // not merely unbuilt: S38 puts environment isolation in the service layer, no query applies it,
  // and a "test" key would read live data — which is why the API refuses to mint one.
  //
  // Leaving this "Soon" was the alternative and it is worse: the route would fall through to the
  // dashboard, so the credential would exist and be unreachable. Shipping a feature and hiding it
  // is not the honest option, it is only the quiet one.
  {
    to: '/app/developer',
    label: 'Developer',
    icon: 'code',
    group: 'organization',
    ready: true,
    // Every route on the screen is `api_keys.manage`.
    requires: ['api_keys.manage'],
  },
  // READY, and it is one screen rather than a claim about an area: endpoints, the delivery log,
  // the failures across all of them, a test delivery and a replay (ADR 0092). It sits beside
  // Developer because the two answer different questions for different people — a developer wants
  // the credential and the reference, an administrator wants to know why an event did not arrive.
  {
    to: '/app/connect',
    label: 'Connect',
    icon: 'activity',
    group: 'organization',
    ready: true,
    // Endpoints, deliveries, replays: all `webhooks.manage`.
    requires: ['webhooks.manage'],
  },
]

const GROUPS: ReadonlyArray<{ readonly id: AreaGroup; readonly label: string }> = [
  { id: 'workspace', label: 'Workspace' },
  // Rendered only when the caller is on at least one team. An empty group heading is worse than no
  // group: it says the product has a concept the reader cannot reach.
  { id: 'team', label: 'Team' },
  { id: 'organization', label: 'Organization' },
]

/**
 * The shell's own `initialsOf(email)` is GONE, and this note is what is left of it.
 *
 * It was up to two initials from an email's local part, and `initialsFrom(name, email)` in
 * `@flowza/ui` — which `Avatar` already calls — does exactly the same thing when the name is null:
 * same separators, same two-letter cap, same `?` for an address it cannot read. Keeping both would
 * have been two copies of one rule in two packages, which this repository has a stated opinion
 * about. `shell.test.tsx` asserts the surviving one against the cases this one was written for.
 */

export function OrgSwitcher({
  orgs,
  activeOrgId,
  onSwitch,
}: {
  readonly orgs: readonly Org[]
  readonly activeOrgId: string
  readonly onSwitch: (orgId: string) => void
}) {
  // Hidden org chrome: one personal org means there is nothing to switch between, and rendering a
  // disabled switcher would invent a concept the user does not have. No orgs at all — the list has
  // not loaded, or the account has none yet — is an empty select with no meaning, not a switcher.
  if (orgs.length === 0) return null
  if (orgs.length === 1 && orgs[0]?.type === 'personal') return null

  return (
    <label className="relative flex items-center">
      <span className="visually-hidden">Active organization</span>
      <Icon
        name="building"
        size={16}
        className="text-text-muted pointer-events-none absolute left-2.5"
      />
      <select
        value={activeOrgId}
        onChange={(e) => onSwitch(e.currentTarget.value)}
        className="h-control border-border-subtle bg-surface-raised text-text-primary hover:border-border-strong max-w-56 appearance-none truncate rounded-md border pl-8 pr-8 text-sm font-medium"
      >
        {orgs.map((org) => (
          <option key={org.id} value={org.id}>
            {org.name}
          </option>
        ))}
      </select>
      <Icon
        name="chevrons-up-down"
        size={14}
        className="text-text-muted pointer-events-none absolute right-2.5"
      />
    </label>
  )
}

/**
 * The team switcher, beside the organization switcher.
 *
 * Hidden when there is nothing to switch between — one team is the state of every organization on
 * its first day, and a disabled switcher would invent a concept the reader does not have yet. That
 * is the same rule `OrgSwitcher` applies one level up, for the same reason.
 *
 * The choice is stored server-side (`user_ui_preferences.active_workspace_id`), so it follows
 * somebody to their second device. A failure to store it is silent: the switch still took effect
 * for this session, and an error banner because a preference did not save would be louder than what
 * it reports.
 */
export function WorkspaceSwitcher({
  workspaces,
  activeWorkspaceId,
  onSwitch,
}: {
  readonly workspaces: readonly Workspace[]
  readonly activeWorkspaceId: string
  readonly onSwitch: (workspaceId: string) => void
}) {
  if (workspaces.length < 2) return null
  return (
    <label className="relative flex items-center">
      <span className="visually-hidden">Active team</span>
      <Icon
        name="folder"
        size={16}
        className="text-text-muted pointer-events-none absolute left-2.5"
      />
      <select
        value={activeWorkspaceId}
        onChange={(e) => onSwitch(e.currentTarget.value)}
        data-testid="workspace-switcher"
        className="h-control border-border-subtle bg-surface-raised text-text-primary hover:border-border-strong max-w-56 appearance-none truncate rounded-md border pl-8 pr-8 text-sm font-medium"
      >
        {workspaces.map((workspace) => (
          <option key={workspace.id} value={workspace.id}>
            {workspace.name}
          </option>
        ))}
      </select>
      <Icon
        name="chevrons-up-down"
        size={14}
        className="text-text-muted pointer-events-none absolute right-2.5"
      />
    </label>
  )
}

/** The org's branding, as the shell holds it and hands it to whatever the outlet renders. */
export type BrandingProfile = {
  readonly id: string
  readonly name: string
  readonly primary_color: string | null
  readonly theme_tokens: Readonly<Record<string, string>> | null
  /**
   * The logo's FACTS (ADR 0081): the bytes are `GET /v1/organization/branding/logo`, fetched by
   * `usePhotoUrl` with `?v=<sha256>`. Null until one is uploaded; absent from a Worker older than
   * the column, which the branding page reads as null.
   */
  readonly logo?: {
    readonly sha256: string
    readonly content_type: string
    readonly width: number
    readonly height: number
  } | null
}

export type BrandingState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly profile: BrandingProfile | null }

/**
 * What the shell passes through `<Outlet />`.
 *
 * `onChange` exists so the page that EDITS branding can hand the saved profile back up: the shell
 * repaints the console and every other consumer sees the new value, without anyone re-fetching it.
 */
export type ShellOutletContext = {
  readonly branding: BrandingState
  readonly onBrandingChange: (profile: BrandingProfile) => void
  /**
   * Paint an accent NOW, before anything is saved.
   *
   * `onBrandingChange` repaints from a stored profile, so it cannot fire until the API has
   * answered — which made the palette picker dead for the length of a round trip and dead
   * altogether wherever the write is refused or the Worker is old. That is the same defect
   * `onThemeChange` below was fixed for, and the fix is the same shape: paint on the click, and
   * let the confirmation reconcile.
   *
   * Takes an accent rather than a profile because the caller has only a colour at that moment.
   * Null means "no override", which `resolveAccent` turns into the standard's palette or the
   * product's own — never a blank console.
   */
  readonly onBrandingPreview: (accent: string | null) => void
  readonly theme: UiTheme
  readonly onThemeChange: (theme: UiTheme) => void
  /**
   * How this organization reads a date (ADR 0132) — its default team's `document_date_format`,
   * served on `GET /v1/organization`, which the shell already asks for.
   *
   * Here rather than fetched per screen for the reason `branding` is here: five lists draw a date
   * column and five copies of one request is four too many. Every screen formats through
   * `useOrgDateFormat` in `dates.ts`, so a date in the Inbox, on the dashboard and in the finished
   * PDF are one shape.
   *
   * Carried RAW — the string the API sent, or null — rather than as a `DocumentDateFormat`.
   * Validating it here would put `@flowza/core` in the entry chunk every route shares, which cost
   * the editor budget 0.7 KB of the 0 KB it had (`lint:bundle`, measured). `dates.ts` is lazy and
   * per-screen, it owns what a valid shape is, and it is the only reader — so it validates.
   */
  readonly dateFormat: string | null
}

/**
 * Write the console's accent onto the document, or take it off.
 *
 * THE RESOLUTION ORDER is in `@flowza/ui`'s `resolveAccent` and not here: a colour the tenant CHOSE
 * beats the standard's palette, which beats the product's own (ADR 0060 — "FlowZa is the absence of
 * an override", so the third case writes nothing rather than writing the product hex).
 *
 * `workflowStandard` is passed rather than read, so this stays a pure function of two inputs and a
 * caller cannot forget one: the international palette applies only where the tenant has set no
 * colour, and a `paint` that did not know the standard would silently be the old behaviour.
 */
function paint(
  tokens: Readonly<Record<string, string>> | null,
  primaryColor: string | null,
  workflowStandard: string,
): void {
  applyResolvedAccent(document.documentElement.style, {
    primaryColor,
    workflowStandard,
    themeTokens: tokens,
  })
}

/**
 * The reader's theme lives in `src/theme.ts`, not here: `main.tsx` applies the cached one before
 * React renders, so the sign-in page is already the reader's theme rather than the device's. The
 * shell's job is the half that needs a session — reconciling against the account's stored
 * preference, and writing a new choice back. Re-exported because `Settings.tsx` types its picker
 * against `ShellOutletContext` and takes the union from the same import.
 */
export type { UiTheme } from '../../theme'

export default function Shell({
  orgs = [],
  activeOrgId = '',
}: {
  readonly orgs?: readonly Org[]
  readonly activeOrgId?: string
}) {
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [branding, setBranding] = useState<BrandingState>({ kind: 'loading' })
  const [workspaces, setWorkspaces] = useState<readonly Workspace[]>([])
  // Teams are a FEATURE an organization turns on (20260913000200). Off, the Team group and the
  // switcher come out of the chrome and nothing else changes: every screen stays reachable and
  // every document stays where it is filed, because OFF is a chrome gate and never a visibility
  // one. Defaulted FALSE so a build pointed at an older Worker — which returns no such field —
  // shows the shell it has always shown rather than inventing a feature.
  const [workspacesEnabled, setWorkspacesEnabled] = useState(false)
  // Null until the organization answers (ADR 0132); `useOrgDateFormat` reads that as the day-first
  // default, so a list drawn before the request lands does not change shape a moment later.
  const [dateFormat, setDateFormat] = useState<string | null>(null)
  const [activeWorkspaceId, setActiveWorkspaceId] = useState('')
  // Seeded from the cache, which is the same value `main.tsx` already put on the document — so the
  // picker in Settings opens on the theme the reader is actually looking at. Reconciled with the
  // server below.
  const [theme, setTheme] = useState<UiTheme>(cachedTheme)

  const workflow = useWorkflow()

  const onBrandingChange = useCallback(
    (profile: BrandingProfile) => {
      setBranding({ kind: 'ready', profile })
      paint(
        profile.theme_tokens ?? null,
        profile.primary_color ?? null,
        workflow.profile.workflow_standard,
      )
    },
    [workflow.profile.workflow_standard],
  )

  /**
   * Called by Settings the moment the reader picks a colour, and superseded by `onBrandingChange`
   * when the API confirms it.
   *
   * Optimistic on purpose, for exactly the reason `onThemeChange` below is. The palette picker used
   * to repaint only inside the `.then()` of `PUT /v1/organization/branding`, so:
   *
   *   * a slow write left the control looking dead for the length of the round trip — measured at
   *     ~1.6 s on production for an authenticated request;
   *   * a REFUSED write (no `branding.manage`, or `branding_locked`) changed nothing at all, which
   *     is correct, but so did a write that failed for any other reason;
   *   * and the reader's own evidence that the click registered was a colour that did not move.
   *
   * A preview that could not be saved is put back by the caller, which is what keeps this honest:
   * the console must never end up painted a colour the organization does not have.
   */
  const onBrandingPreview = useCallback(
    (accent: string | null) => {
      paint(null, accent, workflow.profile.workflow_standard)
    },
    [workflow.profile.workflow_standard],
  )

  /**
   * Called by Settings the moment the reader picks a theme, and again when the API confirms it.
   *
   * It used to be called only after the API had stored the choice. That made the control dead
   * wherever `/v1/profile/preferences` is not deployed — which on production it was not, because
   * this app rebuilds on every push and its Worker lands later. The theme is one viewer's
   * preference and this browser is where it lives; the server copy carries it to a second device.
   * See the note on `AppearanceSection` for the whole argument.
   */
  const onThemeChange = useCallback((next: UiTheme) => {
    setTheme(next)
    applyTheme(next)
    cacheTheme(next)
  }, [])
  const session = useSession()
  // Unconditional, and cheap on purpose: the endpoint answers 404 for anybody with no photo, the
  // hook turns that into null, and `Avatar` paints initials. Asking the profile first whether a
  // photo exists would be a second round trip to save one that mostly 404s in a few hundred bytes.
  const photoUrl = usePhotoUrl(session.kind === 'signed_in' ? '/v1/profile/photo' : null)
  const navigate = useNavigate()
  const location = useLocation()
  // A module opened from the sidebar opens at its top, not at the offset the last screen left.
  useScrollResetOnNavigate()

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault()
        setPaletteOpen((open) => !open)
      }
      if (event.key === 'Escape') {
        setPaletteOpen(false)
        setMenuOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  /**
   * Paint the console in the tenant's colours, once, on entering the shell.
   *
   * Without this the branding was stored, returned by the API, shown in the Settings form — and
   * applied only by the act of saving it. Reload and the console went back to FlowZa blue while
   * Settings still displayed the tenant's colour, which is the worst of both: the setting looks
   * honoured and is not. Measured on production before the fix: the saved accent was `#7c3aed`
   * and `--accent` on the document read `#2f5fd8`, the product default.
   *
   * It lives on the SHELL rather than in Settings because branding is a property of the console,
   * not of the page that happens to edit it.
   *
   * A failure is silent on purpose. Unbranded is the product's own design, which is a perfectly
   * good screen; an error banner because an accent colour did not load would be louder than the
   * thing it reports.
   *
   * The result is handed DOWN through the router outlet rather than fetched again by whoever needs
   * it. Settings was doing exactly that — one request for the shell, one for the editor on the same
   * page — and at a measured ~1.6 s per authenticated request that second round trip is not free.
   */
  useEffect(() => {
    if (!apiConfigured()) return
    let cancelled = false
    api<{ readonly data: BrandingProfile | null }>('/v1/organization/branding')
      .then((page) => {
        if (cancelled) return
        setBranding({ kind: 'ready', profile: page.data })
        paint(
          page.data?.theme_tokens ?? null,
          page.data?.primary_color ?? null,
          workflow.profile.workflow_standard,
        )
      })
      .catch(() => {
        if (!cancelled) setBranding({ kind: 'ready', profile: null })
      })
    return () => {
      cancelled = true
    }
    // Re-runs when the STANDARD changes as well as on mount. A tenant switched to the international
    // standard who has chosen no colour of their own must repaint, and the branding row itself has
    // not moved — so a dependency list of `[]` would leave the console in the old palette until the
    // reload below lands, which is a second of the wrong product.
  }, [workflow.profile.workflow_standard])

  /**
   * The reader's own theme, reconciled on entering the shell.
   *
   * `main.tsx` has already applied the cached value before React rendered, so the document is
   * correct from the first paint and this effect is about the SECOND source: the account's stored
   * preference, which is the one that crosses devices. `applyTheme(theme)` is kept as the opening
   * line anyway — it is a no-op when the boot already wrote the same value, and it is the line that
   * is right if this component is ever mounted outside that entry.
   *
   * A failure is silent for the same reason branding's is: `system` is a perfectly good screen, and
   * an error banner because a theme preference did not load would be louder than what it reports.
   * It also fails silently for an account whose organization is still provisioning, where every
   * authenticated route 404s and none of it is worth a word to the reader.
   */
  useEffect(() => {
    applyTheme(theme)
    if (!apiConfigured()) return
    let cancelled = false
    api<{ readonly theme: string }>('/v1/profile/preferences')
      .then((prefs) => {
        if (cancelled || !isUiTheme(prefs.theme)) return
        setTheme(prefs.theme)
        applyTheme(prefs.theme)
        cacheTheme(prefs.theme)
      })
      .catch(() => {
        // Keep whatever the cache gave us.
      })
    return () => {
      cancelled = true
    }
    // Mount only. `theme` is read for the first paint and then owned by `onThemeChange`; listing it
    // would re-run the fetch on every change the reader makes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * The caller's teams, and which one they were last working in.
   *
   * Two reads rather than one, and both are allowed to fail silently: a console that could not list
   * teams still works — every screen is already narrowed by the caller's own scope server-side (ADR
   * 0066 / D11) — and the switcher simply does not appear. An error banner because a switcher did
   * not load would be louder than what it reports, which is the same posture branding and theme
   * take a few lines above.
   */
  useEffect(() => {
    if (!apiConfigured()) return
    let cancelled = false
    api<{ readonly data?: readonly Workspace[] }>('/v1/workspaces')
      .then((page) => {
        if (cancelled) return
        // Defended rather than trusted. A build whose API URL points at an older Worker gets a 404
        // body with no `data`, and a switcher that threw on it would take the whole shell down —
        // which is the one component that must render whatever else is broken.
        const rows = Array.isArray(page?.data) ? page.data : []
        setWorkspaces(rows)
        setActiveWorkspaceId((current) =>
          current !== '' ? current : (rows.find((w) => w.is_default)?.id ?? rows[0]?.id ?? ''),
        )
      })
      .catch(() => {})
    api<{ readonly workspaces_enabled?: boolean; readonly date_format?: string }>(
      '/v1/organization',
    )
      .then((org) => {
        if (cancelled) return
        setWorkspacesEnabled(org?.workspaces_enabled === true)
        // Absent from an older Worker's answer (S27 — Pages is live in two minutes and the Worker
        // in ten), and the default stands in that case rather than the browser's own order.
        setDateFormat(org?.date_format ?? null)
      })
      .catch(() => {})
    api<{ readonly active_workspace_id?: string | null }>('/v1/profile/preferences')
      .then((prefs) => {
        if (cancelled || typeof prefs?.active_workspace_id !== 'string') return
        setActiveWorkspaceId(prefs.active_workspace_id)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  const onWorkspaceSwitch = useCallback((workspaceId: string) => {
    setActiveWorkspaceId(workspaceId)
    // Stored so the choice follows somebody to their second device. Silent on failure: the switch
    // has already taken effect for this session.
    void api('/v1/profile/preferences', {
      method: 'PATCH',
      body: { active_workspace_id: workspaceId },
    }).catch(() => {})
  }, [])

  /**
   * A workspace created from the International sidebar's switcher (ADR 0095). Appended and made
   * active without a re-read, and the feature switch flips with it: the route only accepted the
   * create because teams are on — or were just turned on by the same dialog — so the TEAM group
   * may appear at once rather than after a reload.
   */
  const onWorkspaceCreated = useCallback(
    (workspace: Workspace) => {
      setWorkspaces((list) =>
        list.some((w) => w.id === workspace.id) ? list : [...list, workspace],
      )
      setWorkspacesEnabled(true)
      onWorkspaceSwitch(workspace.id)
    },
    [onWorkspaceSwitch],
  )

  // A navigation closes whatever was open: the drawer on a phone, the palette anywhere.
  useEffect(() => {
    setMenuOpen(false)
    setPaletteOpen(false)
  }, [location.pathname])

  // See the nav filter below for why the second clause is load-bearing rather than defensive.
  const teamsVisible = workspacesEnabled || workspaces.length > 1

  // What the signed-in person may OPEN (ADR 0196), read once by `PermissionsProvider` around this
  // shell. With no provider — a shell drawn on its own — everything is offered and each screen's
  // own 403 decides, which is also what a failed read answers. Areas that name no key are every
  // member's; the rest are offered to somebody who holds at least one of the keys they name.
  const offering = usePermissions() ?? UNKNOWN_OFFERING
  const visibleAreas = AREAS.filter((area) => offersAny(offering, area.requires))

  // Which sidebar (ADR 0095). `flowza_sign` draws the nav it always had, byte for byte; the
  // International standard draws the workspace-shaped one, and the palette lists whichever is up.
  const international = workflow.profile.workflow_standard === 'international'
  // The palette lists every destination, the two shelves included: they left the SIDEBAR, not the
  // product, and "trash" typed into ⌘K must still go to the bin.
  const paletteAreas = international
    ? internationalPaletteAreas(workspaces.length > 0 && teamsVisible, offering)
    : [...visibleAreas, ...SHELF_AREAS]

  const activeOrg = orgs.find((org) => org.id === activeOrgId) ?? null

  return (
    <div className="bg-surface-base flex min-h-screen">
      {menuOpen ? (
        <div
          aria-hidden="true"
          className="bg-text-primary/30 fixed inset-0 z-20 md:hidden"
          onClick={() => setMenuOpen(false)}
        />
      ) : null}

      {/*
        On a desktop the sidebar is one screen tall and stays put; its nav scrolls inside it.

        It used to be `md:static`, which stretched it to the page's height and scrolled it away with
        the page. Two things followed. Reaching a module low in the nav meant scrolling the WHOLE
        page down first, and the page could never be shorter than the nav, so a short screen sat
        on a tall stretch of empty space. Pick a module from down there and the next screen opened
        on that empty space — blank, black in the dark theme. The scroll reset in
        `useScrollResetOnNavigate` fixes the landing. This fixes the cause: the nav is always on
        screen, so you never scroll the page to reach it. The phone drawer already worked this way.
      */}
      <aside
        className={`border-border-subtle bg-surface-sunken fixed inset-y-0 left-0 z-30 flex w-64 shrink-0 flex-col border-r transition-transform md:sticky md:top-0 md:z-auto md:h-dvh md:translate-x-0 ${
          menuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar"
      >
        <div className="border-border-subtle flex h-14 items-center justify-between border-b px-4">
          <NavLink to="/app" end className="rounded-md" aria-label="FlowZa Sign — dashboard">
            <Wordmark size="sm" />
          </NavLink>
          <button
            type="button"
            aria-label="Close menu"
            className="text-text-muted hover:bg-surface-raised hover:text-text-primary flex size-8 items-center justify-center rounded-md md:hidden"
            onClick={() => setMenuOpen(false)}
          >
            <Icon name="x" size={18} />
          </button>
        </div>

        {international ? (
          <InternationalNav
            workspaces={workspaces}
            activeWorkspaceId={activeWorkspaceId}
            workspacesEnabled={workspacesEnabled}
            teamsVisible={teamsVisible}
            onSwitch={onWorkspaceSwitch}
            onCreated={onWorkspaceCreated}
            onNavigate={() => setMenuOpen(false)}
            offering={offering}
          />
        ) : (
          <nav aria-label="Main" className="flex-1 overflow-y-auto px-3 pb-4">
            {GROUPS.filter(
              // The TEAM group appears when the organization USES teams and the caller is on one. An
              // empty group heading says the product has a concept the reader cannot reach, which is
              // worse than no heading.
              //
              // `workspaces.length > 1` is an OR and not a tidy-up: an organization cannot hold a
              // second team with the feature off (trg_organizations__workspaces_in_use refuses it),
              // so a caller who can see two teams is on an organization that has it on — and that
              // reading survives an older Worker that returns no `workspaces_enabled` at all. The
              // failure it prevents is the one that matters: chrome disappearing from under somebody
              // whose work is filed on a team.
              (group) => group.id !== 'team' || (workspaces.length > 0 && teamsVisible),
            ).map((group) => (
              <div key={group.id} className="mt-4 first:mt-1">
                <p className="text-text-muted m-0 mb-1 px-2.5 text-xs font-medium uppercase tracking-wide">
                  {group.label}
                </p>
                <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
                  {visibleAreas
                    .filter((area) => area.group === group.id)
                    .map((area) => (
                      <li key={area.to}>
                        <AreaLink area={area} onNavigate={() => setMenuOpen(false)} />
                      </li>
                    ))}
                </ul>
              </div>
            ))}
          </nav>
        )}

        {/*
          "Collaborate with your teammates", at the foot of the nav.

          It opens the admin console's invite screen rather than a dialog of its own: the invite
          dialog needs the plan's limits, the team list and the role map, and a second copy of it in
          the shell would be a second place for all three to drift. One destination, one dialog.

          Hidden when the caller holds no team at all, because there is nothing to invite anybody
          INTO yet — and the organization's own default team is created with the organization, so
          "no teams" means the list has not loaded rather than that none exist.

          And hidden from somebody who may neither invite nor ask to (ADR 0196): the screen behind
          it would only tell them so. `mayInvite` keeps the door for a member whose team leaves
          asking on, which is the ask-to-invite flow of ADR 0106.
        */}
        {workspaces.length > 0 && mayInvite(offering) ? (
          <div className="border-border-subtle border-t px-3 py-3">
            <div className="bg-surface-raised border-border-subtle rounded-lg border p-3">
              <p className="text-text-primary m-0 text-sm font-medium">
                Collaborate with your teammates
              </p>
              <p className="text-text-muted m-0 mt-0.5 text-xs">
                Invite colleagues to share documents, templates and contacts.
              </p>
              <NavLink
                to="/app/admin/members"
                onClick={() => setMenuOpen(false)}
                className="text-accent mt-2 inline-flex items-center gap-1 text-sm font-medium"
              >
                Invite Members
                <Icon name="chevron-right" size={14} />
              </NavLink>
            </div>
          </div>
        ) : null}
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header
          data-testid="topbar"
          className="border-border-subtle bg-surface-base/90 sticky top-0 z-10 flex h-14 items-center gap-3 border-b px-4 backdrop-blur lg:px-6"
        >
          <button
            type="button"
            aria-label="Open menu"
            className="text-text-muted hover:bg-surface-sunken hover:text-text-primary flex size-9 items-center justify-center rounded-md md:hidden"
            onClick={() => setMenuOpen(true)}
          >
            <Icon name="menu" size={20} />
          </button>

          <OrgSwitcher orgs={orgs} activeOrgId={activeOrgId} onSwitch={() => {}} />

          {international ? null : (
            <WorkspaceSwitcher
              workspaces={workspaces}
              activeWorkspaceId={activeWorkspaceId}
              onSwitch={onWorkspaceSwitch}
            />
          )}

          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            aria-keyshortcuts="Meta+K Control+K"
            className="border-border-subtle bg-surface-sunken text-text-muted hover:border-border-strong hover:bg-surface-raised hover:text-text-primary ease-standard h-control hidden w-80 items-center gap-2 rounded-md border px-3 text-sm transition duration-150 sm:flex"
          >
            <Icon name="search" size={16} />
            <span className="flex-1 truncate text-left">Search or jump to…</span>
            <Kbd>⌘K</Kbd>
          </button>

          <div className="flex-1" />

          {/*
            The account, at the top right.

            It sat at the foot of the left nav until 2026-09-11, where it is a perfectly good place
            for it and not where anybody looks: every other product in this category puts the
            signed-in identity and the way out of it in the top-right corner, and a person hunting
            for "sign out" hunts there first. Founder's instruction, and the convention is the
            argument.

            `data-testid="signed-in-as"` moves with it deliberately — the test that reads it is
            asserting WHO IS SIGNED IN, which is still true here, so re-pointing the test is right
            and inventing a second id for the same fact would not be.

            The email is hidden below `sm`: on a phone the row is the avatar and the door, and an
            address long enough to matter would push the sign-out control off the edge.
          */}
          {session.kind === 'signed_in' ? (
            <div className="flex items-center gap-2">
              {/*
                The picture, when there is one. `usePhotoUrl` answers null for the overwhelmingly
                common case of somebody who has never set one, and `Avatar` then paints exactly the
                initials this used to paint by hand — so the header is unchanged for every existing
                user and gains a face for anyone who adds one.

                `name={null}`: the shell has an address and no display name, which is what
                the shell always read here. Passing the email alone keeps the initials identical
                to what they were rather than changing them as a side effect of this feature.
              */}
              <Avatar email={session.email} src={photoUrl} size="md" />
              <div className="hidden min-w-0 sm:block">
                <p
                  className="text-text-primary m-0 max-w-48 truncate text-sm font-medium"
                  data-testid="signed-in-as"
                >
                  {session.email ?? 'Signed in'}
                </p>
                <p className="text-text-muted m-0 max-w-48 truncate text-xs">
                  {activeOrg?.name ?? 'Signed in'}
                </p>
              </div>
              <button
                type="button"
                aria-label="Sign out"
                title="Sign out"
                className="text-text-muted hover:bg-surface-sunken hover:text-text-primary flex size-9 shrink-0 items-center justify-center rounded-md"
                onClick={() => {
                  void signOut().then(() => navigate('/auth/login', { replace: true }))
                }}
              >
                <Icon name="log-out" size={16} />
              </button>
            </div>
          ) : null}
        </header>

        {/*
          One banner for the whole console when the API is on an older release than this build,
          shown to owners and admins only. It replaces nothing: every screen still says its own
          "not available on this deployment yet" where it applies. See ReleaseGap.tsx.
        */}
        <ReleaseGapBanner />

        <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <Outlet
            context={
              {
                branding,
                onBrandingChange,
                onBrandingPreview,
                theme,
                onThemeChange,
                dateFormat,
              } satisfies ShellOutletContext
            }
          />
        </main>
      </div>

      {/*
        FlowZa switched this organization's standard while somebody was looking at the console. The
        page is about to reload — see `WorkflowProvider` for why a reload rather than a re-render —
        so this is the one line that says so before it vanishes.

        `role="status"`, not `alert`: nothing is wrong, and interrupting a screen reader mid-sentence
        to announce a theme change would be worse than the change.
      */}
      {workflow.switching ? (
        <div
          role="status"
          aria-live="polite"
          className="bg-surface-raised border-border-strong text-text-primary shadow-overlay fixed inset-x-0 bottom-4 z-50 mx-auto w-[min(32rem,calc(100%-2rem))] rounded-md border px-4 py-3 text-sm"
        >
          {workflow.profile.workflow_standard === 'international'
            ? 'Your organization switched to the International standard — reloading…'
            : 'Your organization switched to the FlowZa Sign standard — reloading…'}
        </div>
      ) : null}

      {paletteOpen ? (
        <Palette
          areas={paletteAreas}
          onClose={() => setPaletteOpen(false)}
          onGo={(to) => {
            setPaletteOpen(false)
            navigate(to)
          }}
        />
      ) : null}
    </div>
  )
}

function Palette({
  areas,
  onClose,
  onGo,
}: {
  /** The destinations of whichever sidebar is up (ADR 0095). */
  readonly areas: readonly Area[]
  readonly onClose: () => void
  readonly onGo: (to: string) => void
}) {
  const [query, setQuery] = useState('')
  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return needle === '' ? areas : areas.filter((area) => area.label.toLowerCase().includes(needle))
  }, [areas, query])

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      className="bg-text-primary/30 fixed inset-0 z-40 flex items-start justify-center px-4 pt-[12vh]"
      onClick={onClose}
    >
      <div
        className="border-border-strong bg-surface-raised w-full max-w-lg overflow-hidden rounded-lg border shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-border-subtle focus-within:border-accent flex items-center gap-2 border-b px-3">
          <Icon name="search" size={16} className="text-text-muted" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            onKeyDown={(e) => {
              const first = matches[0]
              if (e.key === 'Enter' && first !== undefined) {
                e.preventDefault()
                onGo(first.to)
              }
            }}
            aria-label="Where to?"
            placeholder="Type where to go…"
            className="text-text-primary h-11 flex-1 bg-transparent text-base outline-none"
          />
          <Kbd>Esc</Kbd>
        </div>
        {/* Navigation actions only in this phase. A palette that offers "Void envelope" before
            envelopes exist teaches people it lies. */}
        <p className="text-text-muted m-0 px-3 pb-1 pt-3 text-xs font-medium uppercase tracking-wide">
          Go to
        </p>
        <ul className="m-0 list-none p-1.5 pt-0">
          {matches.map((area) => (
            <li key={area.to}>
              <NavLink
                to={area.to}
                end={area.end ?? false}
                onClick={onClose}
                className="text-text-primary hover:bg-surface-sunken flex h-9 items-center gap-2.5 rounded-md px-2.5 text-sm"
              >
                <Icon name={area.icon} size={16} className="text-text-muted" />
                <span className="flex-1">{area.label}</span>
                {!area.ready ? <span className="text-text-muted text-xs">Soon</span> : null}
                <Icon name="chevron-right" size={14} className="text-text-muted" />
              </NavLink>
            </li>
          ))}
          {matches.length === 0 ? (
            <li className="text-text-muted px-2.5 py-3 text-sm">Nothing here matches “{query}”.</li>
          ) : null}
        </ul>
        <div className="border-border-subtle text-text-muted flex items-center gap-4 border-t px-3 py-2 text-xs">
          <span className="inline-flex items-center gap-1">
            <Kbd>↵</Kbd> open
          </span>
          <span className="inline-flex items-center gap-1">
            <Kbd>Esc</Kbd> close
          </span>
        </div>
      </div>
    </div>
  )
}
